import {
  AccessibilityNew,
  BedtimeOutlined,
  ExpandMore,
  FavoriteBorder,
  PsychologyOutlined,
  RestaurantOutlined,
  SpaOutlined,
  WaterDropOutlined,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ComponentType, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HORMONE_TRENDS_LEFT_GUTTER_WIDTH,
  HORMONE_TRENDS_RIGHT_GUTTER_WIDTH,
  groupSymptomRowsByCategory,
} from '../../../common/utils/services/hormone-trends-utils';
import {
  DayColumn,
  RenderedSymptomCell,
  SymptomRow,
} from '../../../common/model/type/hormone-trends.type';
import { SymptomIntensity } from '../../../common/model/type/symptom-intensity.type';
import { SymptomCategoryKey } from '../../../common/symptom-catalogue';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

const DOT_SIZE = 10;

const MIN_DOT_OPACITY = 0.2;

const CATEGORY_ICONS: Record<SymptomCategoryKey, ComponentType> = {
  body: AccessibilityNew,
  diet_and_digestion: RestaurantOutlined,
  mental_wellbeing: PsychologyOutlined,
  sex: FavoriteBorder,
  sleep: BedtimeOutlined,
  skin_and_hair: SpaOutlined,
  urination: WaterDropOutlined,
};

const getRenderedCell = (
  row: SymptomRow,
  index: number
): RenderedSymptomCell | null => {
  const intensity = row.cells[index] as SymptomIntensity;

  if (!intensity || intensity.state === 'not_reported') {
    return null;
  }

  return {
    intensity,
    valueLabel: row.valueLabels[index]?.join(', ') ?? `${intensity.score}`,
  };
};

const dotOpacity = (ratio: number): number =>
  MIN_DOT_OPACITY + ratio * (1 - MIN_DOT_OPACITY);

export interface OvSymptomGridProps {
  columns: DayColumn[];
  rows: SymptomRow[];
  leftGutterWidth?: number;
  rightGutterWidth?: number;
}

const OvSymptomGrid: FC<OvSymptomGridProps> = ({
  columns,
  rows,
  leftGutterWidth = HORMONE_TRENDS_LEFT_GUTTER_WIDTH,
  rightGutterWidth = HORMONE_TRENDS_RIGHT_GUTTER_WIDTH,
}) => {
  const { t } = useTranslation();
  const groups = groupSymptomRowsByCategory(rows);

  const [collapsedCategories, setCollapsedCategories] = useState<
    Set<SymptomCategoryKey>
  >(new Set());

  if (columns.length === 0 || groups.length === 0) {
    return null;
  }

  const toggleCategory = (category: SymptomCategoryKey) => {
    setCollapsedCategories((current) => {
      const next = new Set(current);

      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }

      return next;
    });
  };

  return (
    <Container
      data-testid="symptom-grid"
      data-column-count={columns.length}
      data-column-keys={columns.map((column) => column.key).join(',')}
    >
      {groups.map((group) => {
        const CategoryIcon = CATEGORY_ICONS[group.category];
        const isCollapsed = collapsedCategories.has(group.category);

        return (
          <Category key={group.category}>
            <CategoryHeader
              type="button"
              $columnCount={columns.length}
              $leftGutterWidth={leftGutterWidth}
              $rightGutterWidth={rightGutterWidth}
              aria-expanded={!isCollapsed}
              data-testid={`symptom-category-${group.category}`}
              onClick={() => toggleCategory(group.category)}
            >
              <CategoryLabel>
                <Chevron $isCollapsed={isCollapsed} />
                <CategoryIcon />
                {t(group.labelKey)}
              </CategoryLabel>
            </CategoryHeader>

            <RowsWrapper
              $isCollapsed={isCollapsed}
              aria-hidden={isCollapsed}
              data-testid={`symptom-rows-${group.category}`}
            >
              <RowsInner>
                {group.rows.map((row) => (
                  <SymptomRowElement
                    key={row.symptom}
                    $columnCount={columns.length}
                    $leftGutterWidth={leftGutterWidth}
                    $rightGutterWidth={rightGutterWidth}
                    data-testid={`symptom-row-${row.symptom}`}
                  >
                    <SymptomLabel title={t(row.labelKey)}>
                      {t(row.labelKey)}
                    </SymptomLabel>
                    {columns.map((column, index) => {
                      const cell = getRenderedCell(row, index);

                      return (
                        <Cell
                          key={column.key}
                          data-column-key={column.key}
                          data-testid={`symptom-cell-${row.symptom}-${column.key}`}
                        >
                          {cell && (
                            <Tooltip title={cell.valueLabel} arrow>
                              <Dot
                                role="img"
                                aria-label={`${t(row.labelKey)}: ${
                                  cell.valueLabel
                                }`}
                                $opacity={dotOpacity(cell.intensity.ratio)}
                                data-intensity-logic={cell.intensity.logic}
                                data-intensity-state={cell.intensity.state}
                                data-direction={
                                  cell.intensity.logic === 'directional'
                                    ? cell.intensity.direction
                                    : undefined
                                }
                              />
                            </Tooltip>
                          )}
                        </Cell>
                      );
                    })}
                    <RightSpacer />
                  </SymptomRowElement>
                ))}
              </RowsInner>
            </RowsWrapper>
          </Category>
        );
      })}
    </Container>
  );
};

export default OvSymptomGrid;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const Category = styled.section``;

const gridColumns = (
  columnCount: number,
  leftGutterWidth: number,
  rightGutterWidth: number
): string =>
  `${leftGutterWidth}px repeat(${columnCount}, minmax(0, 1fr)) ${rightGutterWidth}px`;

const CategoryHeader = styled.button<{
  $columnCount: number;
  $leftGutterWidth: number;
  $rightGutterWidth: number;
}>`
  display: grid;
  grid-template-columns: ${({
    $columnCount,
    $leftGutterWidth,
    $rightGutterWidth,
  }) => gridColumns($columnCount, $leftGutterWidth, $rightGutterWidth)};
  width: 100%;
  min-height: 1.75rem;
  padding: 0;
  border: 0;
  background-color: ${Colours.OV_WHITE_HOVER};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: -2px;
  }
`;

const CategoryLabel = styled.span`
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0.25rem 0.5rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
  white-space: nowrap;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Chevron = styled(ExpandMore)<{ $isCollapsed: boolean }>`
  && {
    width: 1rem;
    height: 1rem;
    transform: rotate(${({ $isCollapsed }) => ($isCollapsed ? -90 : 0)}deg);
    transition: transform 0.15s ease;
  }
`;

const RowsWrapper = styled.div<{ $isCollapsed: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isCollapsed }) => ($isCollapsed ? '0fr' : '1fr')};
  transition: grid-template-rows 0.2s ease;
`;

const RowsInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const SymptomRowElement = styled.div<{
  $columnCount: number;
  $leftGutterWidth: number;
  $rightGutterWidth: number;
}>`
  display: grid;
  grid-template-columns: ${({
    $columnCount,
    $leftGutterWidth,
    $rightGutterWidth,
  }) => gridColumns($columnCount, $leftGutterWidth, $rightGutterWidth)};
  min-height: 1.75rem;
  border-bottom: 1px solid ${Colours.OV_WHITE_HOVER};
`;

const SymptomLabel = styled.div`
  display: flex;
  grid-column: 1;
  align-items: center;
  min-width: 0;
  padding: 0.25rem 0.5rem;
  overflow: hidden;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const RightSpacer = styled.div`
  grid-column: -2;
`;

const Dot = styled.span<{ $opacity: number }>`
  display: block;
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  box-sizing: border-box;
  border-radius: ${Variables.borderRadius.CIRCLE};
  background-color: ${Colours.OV_BASE};
  opacity: ${({ $opacity }) => $opacity};
`;
